import {
  LockClosedIcon,
  MagnifyingGlassPlusIcon,
} from '@heroicons/react/24/outline';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { ZoomButtonWrapper } from '../../../../productConfig/productConfigStyles';
import formatUSD from '../../../../../../utils/formatUSD';
import { isJpgImage } from '../../../../../../utils/imageHelpers';
import {
  advanceConfigSlot,
  setBackgroundSelections,
} from '../../../../../../reducers/productConfigSlice';
import { backgroundSlotsCount } from '../../../imageRecords';
import { unlockedGroupsSelector } from '../../../../../../selectors';
import useOrderContainsSingleBackgroundUpgrade from './useOrderContainsSingleBackgroundUpgrade';
import useImageIntentFriction from '../../../../shared/protectedImage/useImageIntentFriction';
import { imageIntentGalleryOf } from '../../../../shared/protectedImage/imageIntentGallery';
import type { ImageIntentContext } from '../../../../../../utils/imageIntentReporter';
import type { Background, Pose, Product } from './types';

interface BackgroundOptionProps {
  background: Background;
  onZoomClick: (e: React.MouseEvent, backgroundId: number) => void;
}

const LockContainer = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 0 50px 50px;
  border-color: transparent transparent rgba(0, 0, 0, 0.5) transparent;
`;

const LockIcon = styled(LockClosedIcon)`
  position: absolute;
  bottom: -44px;
  right: 5px;
  height: auto;
  width: 20px;
  color: white;
`;

const AdjustedZoomButtonWrapper = styled(ZoomButtonWrapper)`
  &:hover {
    background-color: #479de3;
  }
`;

interface BackgroundPriceProps {
  price?: number;
  triggersFreeBackgrounds: boolean;
}

const BackgroundPrice = ({
  price,
  triggersFreeBackgrounds,
}: BackgroundPriceProps) => {
  const orderContainsSingleBackgroundUpgrade =
    useOrderContainsSingleBackgroundUpgrade();

  if (
    triggersFreeBackgrounds ||
    price == null ||
    !Number.isInteger(price) ||
    price <= 0 ||
    orderContainsSingleBackgroundUpgrade
  ) {
    return null;
  }

  return (
    <>
      <br />
      <span className="bnl-bg-price">{formatUSD(price)}</span>
    </>
  );
};

const BackgroundOption = ({
  background,
  onZoomClick,
}: BackgroundOptionProps) => {
  const dispatch = useDispatch();
  const isPrepay = useSelector((state: any) => state.shoot.isPrepay);
  const backgroundSelections = useSelector(
    (state: any) => state.productConfig.backgroundSelections,
  );
  const activeConfigSlotIndex: number | null = useSelector(
    (state: any) => state.productConfig.activeConfigSlotIndex,
  );
  const poseSelections: Pose[] = useSelector(
    (state: any) => state.productConfig.poseSelections,
  );
  const product: Product = useSelector(
    (state: any) => state.productConfig.currentProduct,
  );
  const unlockedGroups: string[] = useSelector(unlockedGroupsSelector);
  const hasActiveConfigSlot =
    activeConfigSlotIndex !== null && activeConfigSlotIndex !== undefined;
  const productBackgroundOptionsCount = backgroundSlotsCount(product);

  const addBackgroundSelection = (bg: Background) => {
    const selectedBackground = product.triggers_free_backgrounds
      ? { ...bg, price: 0 }
      : bg;
    const updatedSelections = [...backgroundSelections];
    // Backgrounds occupy their own dense index space (0..count-1), independent
    // of poses. Without an active slot, fill the first empty background slot,
    // else overwrite the last one.
    let firstEmpty = -1;
    for (let i = 0; i < productBackgroundOptionsCount; i++) {
      if (!updatedSelections[i]) {
        firstEmpty = i;
        break;
      }
    }
    const targetIndex =
      activeConfigSlotIndex !== null
        ? activeConfigSlotIndex
        : firstEmpty !== -1
        ? firstEmpty
        : Math.max(productBackgroundOptionsCount - 1, 0);

    updatedSelections[targetIndex] = selectedBackground;

    while (
      updatedSelections.length > 0 &&
      updatedSelections[updatedSelections.length - 1] === undefined
    ) {
      updatedSelections.pop();
    }

    dispatch(setBackgroundSelections(updatedSelections));

    // advanceConfigSlot reads the selections dispatched just above from the
    // store (dispatch is synchronous), advances to the next unfilled slot,
    // or closes the panel when configuration is complete.
    (dispatch as any)(
      advanceConfigSlot({ slotIndex: targetIndex, step: 'background' }),
    );
  };

  const { studentPreviewImageUrl } = useSelector(
    (state: any) => state.additionalData,
  );
  const isLocked =
    background.lock_type === 'unlockable' &&
    !unlockedGroups.some((set) => background.lock_group.includes(set)) &&
    !(
      product.lock_type === 'unlocker' &&
      product.lock_group.some((set) => background.lock_group.includes(set))
    );

  const lastPose =
    hasActiveConfigSlot &&
    activeConfigSlotIndex !== null &&
    poseSelections[activeConfigSlotIndex]
      ? poseSelections[activeConfigSlotIndex]
      : poseSelections.length > 0
      ? poseSelections[poseSelections.length - 1]
      : null;

  const backgroundOptionStyle: React.CSSProperties = {
    backgroundImage: `url(${background.image_thumbnail})`,
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    position: 'relative',
    cursor: isLocked ? 'default' : 'pointer',
    width: '100%',
    aspectRatio: '4 / 5',
  };

  const imgStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    position: 'absolute',
    top: 0,
    left: 0,
    userSelect: 'none',
    cursor: 'pointer',
  };

  const { getFrictionProps } = useImageIntentFriction({
    surface: 'background_selector',
  });
  // The photo layered over the background reports its own gallery when it
  // was pooled from another one (a sibling's, or none for a group photo);
  // the order's gallery otherwise, including the student preview fallback.
  const frictionContext: ImageIntentContext = {
    surface: 'background_selector',
    imageKey: background.id,
    ...imageIntentGalleryOf(lastPose),
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLocked) {
      return;
    }
    addBackgroundSelection(background);
  };

  const poseImageUrl = lastPose
    ? lastPose.selected_crop
      ? lastPose.selected_crop.url
      : lastPose.url
    : studentPreviewImageUrl;

  // Don't layer JPG images over backgrounds - they don't have alpha transparency
  // JPGs will be converted to PNGs with transparency after order is placed
  const shouldShowPoseOverlay = poseImageUrl && !isJpgImage(poseImageUrl);

  return (
    <div>
      <div
        className="bnl-product-config__bg-container"
        style={backgroundOptionStyle}
        onClick={handleClick}
      >
        {lastPose && shouldShowPoseOverlay && (
          <img
            src={
              lastPose.selected_crop ? lastPose.selected_crop.url : lastPose.url
            }
            alt="Overlay Pose"
            {...getFrictionProps(frictionContext)}
            style={imgStyle}
            onClick={handleClick}
          />
        )}
        {!lastPose &&
          !isPrepay &&
          studentPreviewImageUrl &&
          shouldShowPoseOverlay && (
            <img
              src={studentPreviewImageUrl}
              alt="Overlay Pose"
              {...getFrictionProps(frictionContext)}
              style={imgStyle}
              onClick={handleClick}
            />
          )}
        {isLocked && (
          <LockContainer>
            <LockIcon />
          </LockContainer>
        )}
        <AdjustedZoomButtonWrapper
          onClick={(e: React.MouseEvent) => onZoomClick(e, background.id)}
        >
          <MagnifyingGlassPlusIcon />
        </AdjustedZoomButtonWrapper>
      </div>
      <div className="bnl-bg-text text-center">
        {background.display_name}
        <BackgroundPrice
          price={background.price}
          triggersFreeBackgrounds={product.triggers_free_backgrounds}
        />
      </div>
    </div>
  );
};

export default BackgroundOption;
